// =============================================================================
// Audit action catalog — single source of truth for AuditLog.action strings.
// -----------------------------------------------------------------------------
// The backend writes these strings when recording a business event; the audit
// viewer maps them to a human label + a display tone. New actions use the
// `entity.verb` form. The label/tone maps ALSO cover the older raw strings that
// already exist in the database (capacity_override, update_fulfilment, …) so the
// viewer renders historical rows cleanly — those are intentionally NOT renamed.
// =============================================================================
export const AUDIT_ACTIONS = {
    // Orders
    ORDER_CREATED: 'order.created',
    ORDER_CONFIRMED: 'order.confirmed',
    ORDER_READY: 'order.ready',
    ORDER_DELIVERED: 'order.delivered',
    ORDER_COMPLETED: 'order.completed',
    ORDER_CANCELLED: 'order.cancelled',
    ORDER_LINES_UPDATED: 'order.lines_updated',
    ORDER_NOTES_UPDATED: 'order.notes_updated',
    ORDER_PAYMENT_OVERRIDE_SET: 'order.payment_override_set',
    ORDER_PAYMENT_OVERRIDE_CLEARED: 'order.payment_override_cleared',
    // A pre-go-live paper order back-entered through the legacy-intake endpoint.
    // Warning tone deliberately: this bypasses stock, capacity and past-date
    // validation and keeps a hand-typed document number, so it must never read
    // as routine in the audit log.
    ORDER_LEGACY_INTAKE: 'order.legacy_intake',
    ORDER_RETURNED: 'order.returned',
    // Quotations
    QUOTATION_CREATED: 'quotation.created',
    QUOTATION_FOLLOWED_UP: 'quotation.followed_up',
    QUOTATION_WON: 'quotation.won',
    QUOTATION_LOST: 'quotation.lost',
    QUOTATION_CONVERTED: 'quotation.converted',
    QUOTATION_EXPIRED: 'quotation.expired',
    // Delivery notes
    DELIVERYNOTE_CREATED: 'deliverynote.created',
    DELIVERYNOTE_DISPATCHED: 'deliverynote.dispatched',
    DELIVERYNOTE_SIGNED: 'deliverynote.signed',
    DELIVERYNOTE_APPROVED: 'deliverynote.approved',
    DELIVERYNOTE_REJECTED: 'deliverynote.rejected',
    DELIVERYNOTE_FAILED: 'deliverynote.failed',
    // Payments
    PAYMENT_RECORDED: 'payment.recorded',
    // A deposit collected in cash before go-live, recorded against a legacy order.
    // Distinct from PAYMENT_RECORDED so a NON-POSTING receipt can never be
    // mistaken for real money moving through the system today.
    PAYMENT_LEGACY_RECORDED: 'payment.legacy_recorded',
    PAYMENT_VERIFIED: 'payment.verified',
    PAYMENT_REJECTED: 'payment.rejected',
    PAYMENT_VOIDED: 'payment.voided',
    // Cash close
    CASHCLOSE_CREATED: 'cashclose.created',
    // Driver remittances
    REMITTANCE_SUBMITTED: 'remittance.submitted',
    REMITTANCE_DRAWER_SUBMITTED: 'remittance.drawer_submitted',
    REMITTANCE_ACKNOWLEDGED: 'remittance.acknowledged',
    REMITTANCE_DISPUTED: 'remittance.disputed',
    // Numbered stock units (imported mattresses). The number written on a piece is
    // the thread a warranty claim is pulled by, so every change to one is logged.
    STOCKUNITS_ADDED: 'stockunit.added',
    STOCKUNIT_UPDATED: 'stockunit.updated',
    STOCKUNIT_WRITTEN_OFF: 'stockunit.written_off',
    STOCKUNITS_DISPATCHED: 'stockunit.dispatched',
};
/** Distinct entity types written to AuditLog — used to populate viewer filters. */
export const AUDIT_ENTITY_TYPES = [
    'Order',
    'Quotation',
    'DeliveryNote',
    'Payment',
    'CashCloseEvent',
    'DriverRemittance',
    'DiscountApproval',
    'StockUnit',
];
// Human labels. Includes the legacy raw strings already in the DB so every
// historical row renders with a friendly label in the viewer.
export const AUDIT_ACTION_LABEL = {
    [AUDIT_ACTIONS.ORDER_CREATED]: 'Order created',
    [AUDIT_ACTIONS.ORDER_CONFIRMED]: 'Order confirmed',
    [AUDIT_ACTIONS.ORDER_READY]: 'Marked ready',
    [AUDIT_ACTIONS.ORDER_DELIVERED]: 'Marked delivered',
    [AUDIT_ACTIONS.ORDER_COMPLETED]: 'Order completed',
    [AUDIT_ACTIONS.ORDER_CANCELLED]: 'Order cancelled',
    [AUDIT_ACTIONS.ORDER_LINES_UPDATED]: 'Order lines updated',
    [AUDIT_ACTIONS.ORDER_NOTES_UPDATED]: 'Order note updated',
    [AUDIT_ACTIONS.ORDER_PAYMENT_OVERRIDE_SET]: 'Payment override set',
    [AUDIT_ACTIONS.ORDER_PAYMENT_OVERRIDE_CLEARED]: 'Payment override cleared',
    [AUDIT_ACTIONS.ORDER_LEGACY_INTAKE]: 'Legacy order back-entered',
    [AUDIT_ACTIONS.PAYMENT_LEGACY_RECORDED]: 'Legacy deposit recorded (not posted)',
    [AUDIT_ACTIONS.ORDER_RETURNED]: 'Items returned',
    [AUDIT_ACTIONS.QUOTATION_CREATED]: 'Quotation created',
    [AUDIT_ACTIONS.QUOTATION_FOLLOWED_UP]: 'Quotation followed up',
    [AUDIT_ACTIONS.QUOTATION_WON]: 'Quotation won',
    [AUDIT_ACTIONS.QUOTATION_LOST]: 'Quotation lost',
    [AUDIT_ACTIONS.QUOTATION_CONVERTED]: 'Quotation converted to order',
    [AUDIT_ACTIONS.QUOTATION_EXPIRED]: 'Quotation expired',
    [AUDIT_ACTIONS.DELIVERYNOTE_CREATED]: 'Delivery note created',
    [AUDIT_ACTIONS.DELIVERYNOTE_DISPATCHED]: 'Delivery dispatched',
    [AUDIT_ACTIONS.DELIVERYNOTE_SIGNED]: 'Delivery signed',
    [AUDIT_ACTIONS.DELIVERYNOTE_APPROVED]: 'Delivery approved',
    [AUDIT_ACTIONS.DELIVERYNOTE_REJECTED]: 'Delivery rejected',
    [AUDIT_ACTIONS.DELIVERYNOTE_FAILED]: 'Delivery failed',
    [AUDIT_ACTIONS.PAYMENT_RECORDED]: 'Payment recorded',
    [AUDIT_ACTIONS.PAYMENT_VERIFIED]: 'Payment verified',
    [AUDIT_ACTIONS.PAYMENT_REJECTED]: 'Payment rejected',
    [AUDIT_ACTIONS.PAYMENT_VOIDED]: 'Payment voided',
    [AUDIT_ACTIONS.CASHCLOSE_CREATED]: 'Cash drawer closed',
    [AUDIT_ACTIONS.REMITTANCE_SUBMITTED]: 'Remittance submitted',
    [AUDIT_ACTIONS.REMITTANCE_DRAWER_SUBMITTED]: 'Drawer remittance submitted',
    [AUDIT_ACTIONS.REMITTANCE_ACKNOWLEDGED]: 'Remittance acknowledged',
    [AUDIT_ACTIONS.STOCKUNITS_ADDED]: 'Unit numbers recorded',
    [AUDIT_ACTIONS.STOCKUNIT_UPDATED]: 'Unit number changed',
    [AUDIT_ACTIONS.STOCKUNIT_WRITTEN_OFF]: 'Unit written off',
    [AUDIT_ACTIONS.STOCKUNITS_DISPATCHED]: 'Units loaded for delivery',
    [AUDIT_ACTIONS.REMITTANCE_DISPUTED]: 'Remittance disputed',
    // Legacy strings already present in the DB (kept as-is):
    capacity_override: 'Delivery capacity override',
    update_fulfilment: 'Fulfilment updated',
    reschedule_delivery: 'Delivery rescheduled',
    reschedule_production: 'Production rescheduled',
    change_line_size: 'Line size changed',
    discount_approval_requested: 'Discount approval requested',
    discount_approval_approved: 'Discount approval approved',
    discount_approval_rejected: 'Discount approval rejected',
};
/** Friendly label for an action string; falls back to the raw string. */
export function auditActionLabel(action) {
    return AUDIT_ACTION_LABEL[action] ?? action;
}
// Display tone per action for the viewer's pill. Creation → info, positive
// progressions → positive, cancellations/rejections/voids → negative, edits and
// overrides → warning, everything else → neutral.
export const AUDIT_ACTION_TONE = {
    [AUDIT_ACTIONS.ORDER_CREATED]: 'info',
    [AUDIT_ACTIONS.ORDER_CONFIRMED]: 'info',
    [AUDIT_ACTIONS.ORDER_READY]: 'accent',
    [AUDIT_ACTIONS.ORDER_DELIVERED]: 'positive',
    [AUDIT_ACTIONS.ORDER_COMPLETED]: 'positive',
    [AUDIT_ACTIONS.ORDER_CANCELLED]: 'negative',
    [AUDIT_ACTIONS.ORDER_LINES_UPDATED]: 'warning',
    [AUDIT_ACTIONS.ORDER_NOTES_UPDATED]: 'warning',
    [AUDIT_ACTIONS.ORDER_PAYMENT_OVERRIDE_SET]: 'warning',
    [AUDIT_ACTIONS.ORDER_PAYMENT_OVERRIDE_CLEARED]: 'warning',
    [AUDIT_ACTIONS.ORDER_LEGACY_INTAKE]: 'warning',
    [AUDIT_ACTIONS.PAYMENT_LEGACY_RECORDED]: 'warning',
    [AUDIT_ACTIONS.ORDER_RETURNED]: 'negative',
    [AUDIT_ACTIONS.QUOTATION_CREATED]: 'info',
    [AUDIT_ACTIONS.QUOTATION_FOLLOWED_UP]: 'neutral',
    [AUDIT_ACTIONS.QUOTATION_WON]: 'positive',
    [AUDIT_ACTIONS.QUOTATION_LOST]: 'negative',
    [AUDIT_ACTIONS.QUOTATION_CONVERTED]: 'positive',
    [AUDIT_ACTIONS.QUOTATION_EXPIRED]: 'neutral',
    [AUDIT_ACTIONS.DELIVERYNOTE_CREATED]: 'info',
    [AUDIT_ACTIONS.DELIVERYNOTE_DISPATCHED]: 'accent',
    [AUDIT_ACTIONS.DELIVERYNOTE_SIGNED]: 'positive',
    [AUDIT_ACTIONS.DELIVERYNOTE_APPROVED]: 'positive',
    [AUDIT_ACTIONS.DELIVERYNOTE_REJECTED]: 'negative',
    [AUDIT_ACTIONS.DELIVERYNOTE_FAILED]: 'negative',
    [AUDIT_ACTIONS.PAYMENT_RECORDED]: 'info',
    [AUDIT_ACTIONS.PAYMENT_VERIFIED]: 'positive',
    [AUDIT_ACTIONS.PAYMENT_REJECTED]: 'negative',
    [AUDIT_ACTIONS.PAYMENT_VOIDED]: 'negative',
    [AUDIT_ACTIONS.CASHCLOSE_CREATED]: 'info',
    [AUDIT_ACTIONS.REMITTANCE_SUBMITTED]: 'info',
    [AUDIT_ACTIONS.REMITTANCE_DRAWER_SUBMITTED]: 'info',
    [AUDIT_ACTIONS.REMITTANCE_ACKNOWLEDGED]: 'positive',
    [AUDIT_ACTIONS.REMITTANCE_DISPUTED]: 'negative',
    [AUDIT_ACTIONS.STOCKUNITS_ADDED]: 'info',
    [AUDIT_ACTIONS.STOCKUNIT_UPDATED]: 'warning',
    [AUDIT_ACTIONS.STOCKUNIT_WRITTEN_OFF]: 'negative',
    [AUDIT_ACTIONS.STOCKUNITS_DISPATCHED]: 'accent',
    capacity_override: 'warning',
    update_fulfilment: 'warning',
    reschedule_delivery: 'warning',
    reschedule_production: 'warning',
    change_line_size: 'warning',
    discount_approval_requested: 'info',
    discount_approval_approved: 'positive',
    discount_approval_rejected: 'negative',
};
/** Display tone for an action string; falls back to neutral. */
export function auditActionTone(action) {
    return AUDIT_ACTION_TONE[action] ?? 'neutral';
}
//# sourceMappingURL=audit.js.map